import { Workflow, WorkflowStep } from '../types/workflow';

export interface ExecutionResult {
  stepName: string;
  success: boolean;
  data?: any;
  error?: string;
  duration: number;
}

export interface ExecutionContext {
  variables: Record<string, any>;
  results: ExecutionResult[];
}

export class WorkflowExecutor {
  // Public helper to resolve inputs with provided variables
  public resolveInputsWithVariables(inputs: any, variables: Record<string, any>): any {
    const context: ExecutionContext = { variables, results: [] };
    // reuse internal variable resolver
    // eslint-disable-next-line @typescript-eslint/no-unsafe-return
    return this.resolveVariables(inputs, context);
  }

  // Execute full workflow by following the `next` chain and simulating system nodes
  public async executeWorkflowFullSim(workflow: Workflow, onStepComplete?: (result: ExecutionResult) => void): Promise<ExecutionResult[]> {
    const context: ExecutionContext = { variables: {}, results: [] };

    // Build step map by name for quick lookup
    const steps = workflow.workflow.steps;
    const stepMap: Record<string, WorkflowStep> = {} as any;
    steps.forEach(s => { (stepMap as any)[s.name] = s; });

    // Determine start step: pick the first step in array that is not referenced as `next` by any other
    const referenced = new Set<string>();
    steps.forEach(s => { if ((s as any).next) referenced.add((s as any).next as string); });
    const start = steps.find(s => !referenced.has(s.name)) || steps[0];

    const { CONSTANT_STEPS } = await import('../constants/stepTemplates');
    const isSystem = (name: string) => CONSTANT_STEPS.some(cs => cs.name === name);
    const isEvmConstructOrStep = (s: WorkflowStep) => {
      const img = String((s as any).image || '').toLowerCase();
      const nm = String((s.name || '')).toLowerCase();
      const hay = nm + ' ' + img;
      // Broad match for EVM-related constructs and contracts
      return /\bevm\b|userop|bundler|calldata|contract|erc\d|scw|smart[-_ ]?account/.test(hay);
    };

    // Traverse the chain
    let current: WorkflowStep | undefined = start;
    const visited = new Set<string>();

    while (current && !visited.has(current.name)) {
      visited.add(current.name);

      // If this is a system node or EVM construct/custom EVM, skip entirely
      if (isSystem(current.name) || isEvmConstructOrStep(current)) {
        const nextName = (current as any).next as string | undefined;
        current = nextName ? stepMap[nextName] : undefined;
        continue;
      }

      let execResult: ExecutionResult | undefined;
      try {
        const lowerName = (current.name || '').toLowerCase();
        // Custom/API/AI steps: reuse existing logic
        if ((current as any).type === 10 || lowerName.includes('api')) {
          execResult = await this.executeApiStep(current, context);
        } else if (lowerName.includes('ai')) {
          execResult = await this.executeAiStep(current, context);
        } else {
          execResult = { stepName: current.name, success: true, data: { message: 'Custom step executed (no-op)' }, duration: 0 };
        }

        context.results.push(execResult);
        if (onStepComplete) onStepComplete(execResult);
        if (!execResult.success) break;
      } catch (err: any) {
        const fail: ExecutionResult = { stepName: current.name, success: false, error: err?.message || 'Unknown error', duration: 0 };
        context.results.push(fail);
        if (onStepComplete) onStepComplete(fail);
        break;
      }

      // Move to next
      const nextName = (current as any).next as string | undefined;
      current = nextName ? stepMap[nextName] : undefined;
    }

    return context.results;
  }

  // Public helper to execute all custom steps prior to a target step name
  public async executeWorkflow(workflow: Workflow, onStepComplete?: (result: ExecutionResult) => void): Promise<ExecutionResult[]> {
    const context: ExecutionContext = { variables: {}, results: [] };
    const { CONSTANT_STEPS } = await import('../constants/stepTemplates');
    const isSystem = (name: string) => CONSTANT_STEPS.some(cs => cs.name === name);
    const isEvmConstructOrStep = (s: WorkflowStep) => {
      const img = (s as any).image || '';
      const nm = (s.name || '').toLowerCase();
      return img.includes('executor-encoder-evm@sha256:383b29c86e974c089b8a3dd5d5a8594f2a99e01e214dabe07f97f6989c9f4aa4') || nm.includes('evm');
    };
    const customSteps = workflow.workflow.steps.filter(step => !isSystem(step.name));
    for (const step of customSteps) {
      if (isEvmConstructOrStep(step)) {
        continue; // skip EVM constructs/steps entirely
      }
      let result: ExecutionResult;
      if (step.type === 10 || step.name.includes('api')) {
        result = await this.executeApiStep(step, context);
      } else if (step.name.includes('ai')) {
        result = await this.executeAiStep(step, context);
      } else {
        result = { stepName: step.name, success: true, data: { message: 'Step skipped - not implemented yet' }, duration: 0 };
      }
      context.results.push(result);
      if (onStepComplete) onStepComplete(result);
      if (!result.success) break;
    }

    return context.results;
  }

  // Public helper to execute all custom steps prior to a target step name
  public async executeUntil(workflow: Workflow, stopBeforeStepName: string, onStepComplete?: (result: ExecutionResult) => void): Promise<ExecutionContext> {
    const context: ExecutionContext = { variables: {}, results: [] };
    const { CONSTANT_STEPS } = await import('../constants/stepTemplates');
    const customSteps = workflow.workflow.steps.filter(step => !CONSTANT_STEPS.some(cs => cs.name === step.name));

    for (const step of customSteps) {
      if (step.name === stopBeforeStepName) break;
      let result: ExecutionResult;
      if (step.type === 10 || step.name.includes('api')) {
        result = await this.executeApiStep(step, context);
      } else if (step.name.includes('ai')) {
        result = await this.executeAiStep(step, context);
      } else {
        result = { stepName: step.name, success: true, data: { message: 'Step skipped - not implemented yet' }, duration: 0 };
      }
      context.results.push(result);
      if (onStepComplete) onStepComplete(result);
      if (!result.success) break;
    }

    return context;
  }

  private async executeApiStep(step: WorkflowStep, context: ExecutionContext): Promise<ExecutionResult> {
    const startTime = Date.now();
    
    try {
      // Replace variables in URL and other inputs
      const resolvedInputs = this.resolveVariables(step.inputs, context);
      
      const url = resolvedInputs.url as string;
      const method = (resolvedInputs.method as string) || 'GET';
      const headers = resolvedInputs.headers || {};
      const body = resolvedInputs.body;
      
      // Inject ngrok header to bypass free plan browser warning if the URL is ngrok
      const finalHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        ...headers,
      } as any;
      if (/ngrok(-free)?\.app|ngrok/i.test(url)) {
        (finalHeaders as any)['ngrok-skip-browser-warning'] = 'true';
      }

      const requestOptions: RequestInit = {
        method: method.toUpperCase(),
        headers: finalHeaders
      };
      
      if (method.toUpperCase() !== 'GET' && body) {
        requestOptions.body = typeof body === 'string' ? body : JSON.stringify(body);
      }
      
      // Decide whether to use proxy: route all absolute http(s) URLs via proxy
      const isExternal = /^https?:\/\//i.test(url);

      let response: Response;
      if (isExternal) {
        response = await fetch('/api/proxy', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ url, method, headers: finalHeaders, body }),
        });
      } else {
        response = await fetch(url, requestOptions);
      }
      const contentType = response.headers.get('content-type') || '';
      const resClone = response.clone();
      let responseData: any;
      if (/application\/json/i.test(contentType)) {
        try {
          responseData = await response.json();
        } catch {
          responseData = await resClone.text();
        }
      } else {
        try {
          responseData = await response.text();
        } catch {
          responseData = await resClone.text();
        }
      }
      
      // First, store any exportable inputs in context variables so they can be referenced
      try {
        if (Array.isArray(step.inputs)) {
          (step.inputs as any[]).forEach((inp: any) => {
            const shouldExport = inp?.export === true || ['url','method','headers','body','timeout'].includes(String(inp?.name || ''));
            if (inp?.name && shouldExport) {
              context.variables[`${step.name}.${inp.name}`] = (resolvedInputs as any)[inp.name];
            }
          });
        } else if (step.inputs && typeof step.inputs === 'object') {
          Object.keys(step.inputs as any).forEach((k) => {
            const v = (resolvedInputs as any)[k];
            // object-shaped inputs: export only if explicitly requested via export: true in a sibling meta map (not available), so skip by default
            // no-op
          });
        }
      } catch {
        // ignore input export errors
      }

      // Store outputs in context variables
      if (step.outputs) {
        step.outputs.forEach(output => {
          if (output.export) {
            const value = typeof responseData === 'string' ? responseData : this.extractValue(responseData, output.value);
            context.variables[`${step.name}.${output.name}`] = value;
          }
        });
      }
      
      return {
        stepName: step.name,
        success: response.ok,
        data: responseData,
        duration: Date.now() - startTime
      };
      
    } catch (error) {
      return {
        stepName: step.name,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        duration: Date.now() - startTime
      };
    }
  }
  
  private async executeAiStep(step: WorkflowStep, context: ExecutionContext): Promise<ExecutionResult> {
    const startTime = Date.now();
    
    try {
      // Replace variables in inputs
      const resolvedInputs = this.resolveVariables(step.inputs, context);
      
      const url = resolvedInputs.url as string;
      const method = (resolvedInputs.method as string) || 'POST';
      const headers = resolvedInputs.headers || {};
      const body = resolvedInputs.body;
      const timeout = (resolvedInputs.timeout as number) || 30;
      
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), timeout * 1000);
      
      // Inject ngrok header to bypass free plan browser warning if the URL is ngrok
      const finalHeaders: Record<string, string> = {
        'Content-Type': 'application/json',
        ...headers,
      } as any;
      if (/ngrok(-free)?\.app|ngrok/i.test(url)) {
        (finalHeaders as any)['ngrok-skip-browser-warning'] = 'true';
      }

      // Decide whether to use proxy: route all absolute http(s) URLs via proxy
      const isExternal = /^https?:\/\//i.test(url);

      let response = await (async () => {
        if (isExternal) {
          return fetch('/api/proxy', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({ url, method, headers: finalHeaders, body }),
            signal: controller.signal,
          });
        }
        return fetch(url, {
          method: method.toUpperCase(),
          headers: finalHeaders,
          body: typeof body === 'string' ? body : JSON.stringify(body),
          signal: controller.signal
        });
      })();
      
      clearTimeout(timeoutId);
      const contentType = response.headers.get('content-type') || '';
      const resClone = response.clone();
      let responseData: any;
      if (/application\/json/i.test(contentType)) {
        try {
          responseData = await response.json();
        } catch {
          responseData = await resClone.text();
        }
      } else {
        try {
          responseData = await response.text();
        } catch {
          responseData = await resClone.text();
        }
      }
      
      // First, store any exportable inputs in context variables so they can be referenced
      try {
        if (Array.isArray(step.inputs)) {
          (step.inputs as any[]).forEach((inp: any) => {
            const shouldExport = inp?.export === true || ['url','method','headers','body','timeout'].includes(String(inp?.name || ''));
            if (inp?.name && shouldExport) {
              context.variables[`${step.name}.${inp.name}`] = (resolvedInputs as any)[inp.name];
            }
          });
        } else if (step.inputs && typeof step.inputs === 'object') {
          Object.keys(step.inputs as any).forEach((k) => {
            const v = (resolvedInputs as any)[k];
            // object-shaped inputs: export only if explicitly requested; skip by default
          });
        }
      } catch {
        // ignore input export errors
      }

      // Store outputs in context variables
      if (step.outputs) {
        step.outputs.forEach(output => {
          if (output.export) {
            const value = this.extractValue(responseData, output.value);
            context.variables[`${step.name}.${output.name}`] = value;
          }
        });
      }
      
      return {
        stepName: step.name,
        success: response.ok,
        data: responseData,
        duration: Date.now() - startTime
      };
      
    } catch (error) {
      return {
        stepName: step.name,
        success: false,
        error: error instanceof Error ? error.message : 'Unknown error',
        duration: Date.now() - startTime
      };
    }
  }
  
  private resolveVariables(inputs: any, context: ExecutionContext): any {
    const resolved = JSON.parse(JSON.stringify(inputs));
    
    const resolveString = (str: string): string => {
      return str.replace(/\$\{([^}]+)\}/g, (match, varPath) => {
        const value = context.variables[varPath];
        return value !== undefined ? String(value) : match;
      });
    };
    
    const resolveValue = (value: any): any => {
      if (typeof value === 'string') {
        return resolveString(value);
      } else if (Array.isArray(value)) {
        return value.map(resolveValue);
      } else if (value && typeof value === 'object') {
        const result: any = {};
        Object.keys(value).forEach(key => {
          result[key] = resolveValue(value[key]);
        });
        return result;
      }
      return value;
    };
    
    return resolveValue(resolved);
  }
  
  private extractValue(data: any, path: string): any {
    const parts = path.split('.');
    let current = data;
    
    for (const part of parts) {
      if (current && typeof current === 'object' && part in current) {
        current = current[part];
      } else {
        return undefined;
      }
    }
    
    return current;
  }
}